import Mode from "@zxing/library/esm/core/qrcode/decoder/Mode";
import { BehaviorSubject, Subject } from "rxjs";

export enum TransportOptionConstraintType {
  MIN_SELECTED_COUNT = 'MIN_SELECTED_COUNT',
  MAX_SELECTED_COUNT = 'MAX_SELECTED_COUNT',
}

export class TransportOptionsPanelModel {

  readOnly: boolean = false;
  selectionChange = new Subject<void>();
  valid: boolean = false;
  dirty: boolean = false;
  validState = new BehaviorSubject<boolean>(false);

  loadingEnabled = false;
  loadingMandatory = false;
  parametersEnabled = false;
  parametersMandatory = false;

  optionStatus = new Map();

  messages = new Array<string>();
  columns = new Array<TransportOptionColumn>();
  constraints = new Array<TransportOptionConstraint>();
  selection = new Array<TransportOptionSelection>();

  conditions = new Array<TransportOptionCondition>();
  confirmedConditions = new Array<string>();

  constructor(model?: Partial<TransportOptionsPanelModel>) {
    if (model != null) {

      this.readOnly = model.readOnly || false;
      this.loadingEnabled = model.loadingEnabled || false;
      this.loadingMandatory = model.loadingMandatory || false;
      this.parametersEnabled = model.parametersEnabled || false;
      this.parametersMandatory = model.parametersMandatory || false;

      if (model.columns != null) {
        for (const column of model.columns) {
          this.columns.push(new TransportOptionColumn(column));
        }

        for (const column of this.columns) {
          column.options = column.options.sort((a, b) => {
            return (a.rowIndex - b.rowIndex);
          });
        }
      }
      if (model.constraints != null) {
        for (const constraint of model.constraints) {
          this.constraints.push(new TransportOptionConstraint(constraint));
        }
      }
      if (model.selection != null) {
        for (const selection of model.selection) {
          this.selection.push(new TransportOptionSelection(selection));
        }
      }

    }
    this.validate();
  }

  validate() {

    console.log('revalidating model');

    let messages = new Array<string>();
    this.optionStatus.clear();

    let constraintFailed = false;
    if (!this.checkRequiredFieldsAreValid()) {
      constraintFailed = true;
      messages.push("tmc-yard.entity.transport-option-constraint.messages.required-fields-missing");
    }

    if (!this.checkRequiredLoadingIsValid()) {
      constraintFailed = true;
      messages.push("tmc-yard.entity.transport-option-constraint.messages.required-loading-amount-missing");
    }

    if (!this.checkRequiredParameterIsValid()) {
      constraintFailed = true;
      messages.push("tmc-yard.entity.transport-option-constraint.messages.required-parameter-missing");
    }

    for (const constraint of this.constraints) {

      const selectedOptionsCount = this.countSelectedOptions(constraint.transportOptionIds);
      if (TransportOptionConstraintType.MIN_SELECTED_COUNT === constraint.type) {
        if (selectedOptionsCount < constraint.count) {
          console.warn('validating model failed due to constraint', constraint, selectedOptionsCount);
          constraintFailed = true;
          messages.push("tmc-yard.entity.transport-option-constraint.messages.constraint-failed");
        }
      } else if (TransportOptionConstraintType.MAX_SELECTED_COUNT === constraint.type) {
        if (selectedOptionsCount > constraint.count) {
          console.warn('validating model failed due to constraint', constraint, selectedOptionsCount);
          constraintFailed = true;
          messages.push("tmc-yard.entity.transport-option-constraint.messages.constraint-failed");
        }

      }

    }

    this.valid = !constraintFailed;
    this.messages = messages;
    this.validState.next(this.valid);

  }

  public getOnlySelectedTransportOptionSelection(): Array<TransportOptionSelection> {
    const selection = new Array<TransportOptionSelection>();

    for (const column of this.columns) {
      for (const option of column.options) {
        const optionSelection = this.findSelection(option.id);
        if (optionSelection?.selected === true) {
          selection.push(optionSelection);
          for (const childOption of option.options) {
            const childSelection = this.findSelection(childOption.id);
            if (childSelection?.selected === true) {
              selection.push(childSelection);
            }
          }
        }
      }
    }

    return selection;
  }

  checkRequiredFieldsAreValid(): boolean {
    let result = true;
    for (const column of this.columns) {
      for (const option of column.options) {
        if (this.checkOptionIsSelected(option.id)) {
          for (const subOption of option.options) {
            if (!subOption.optional) {
              if (!this.checkOptionIsSelected(subOption.id)) {
                result = false;
                // TODO add message here
                this.optionStatus.set(subOption.id, 'required');
              }
            }
          }
          if (option.options.length > 0) {
            const selectedChildCount = this.countSelectedOptions(option.options.map(({ id }) => id));
            if (selectedChildCount < option.minimumSelectedOptions) {
              result = false;
              // TODO add message here
              for (const subOption of option.options) {
                this.optionStatus.set(subOption.id, 'required');
              }
            }
          }
        } else {
          if (!option.optional) {
            result = false;
            // TODO add message here
            this.optionStatus.set(option.id, 'required');
          }
        }
      }
    }
    return result;
  }

  checkOptionIsSelected(id: number): boolean {
    for (const selection of this.selection) {
      if (selection.id === id) {
        if (selection.selected === true) {
          return true;
        }
      }
    }
    return false;
  }

  countSelectedOptions(transportOptionIds: number[]): number {
    let result = 0;
    for (const number of transportOptionIds) {
      for (const selection of this.selection) {
        if (selection.id === number) {
          if (selection.selected === true) {
            result += 1;
          }
        }
      }
    }
    return result;
  }

  private checkRequiredLoadingIsValid(): boolean {
    for (const column of this.columns) {
      for (const option of column.options) {
        if (this.checkOptionIsSelected(option.id)) {
          if (!this.checkAmountInputIsValid(option)) {
            return false;
          }
          for (const subOption of option.options) {
            if (this.checkOptionIsSelected(subOption.id) && !this.checkAmountInputIsValid(subOption)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }

  private checkRequiredParameterIsValid(): boolean {
    for (const column of this.columns) {
      for (const option of column.options) {
        if (this.checkOptionIsSelected(option.id)) {
          if (!this.checkParameterIsValid(option)) {
            return false;
          }
          for (const subOption of option.options) {
            if (this.checkOptionIsSelected(subOption.id) && !this.checkParameterIsValid(subOption)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }

  private checkAmountInputIsValid(option: TransportOption) {

    if (!this.loadingEnabled || !this.loadingMandatory) {
      return true;
    }

    const optionSelection = this.findSelection(option.id);
    if (optionSelection != null && optionSelection.selected) {

      if (!option.amountInKilogrammMandatory && !option.amountInLiterMandatory && !option.amountOthersMandatory && !option.amountOfPallettesMandatory && !option.amountOfBoxesMandatory) {
        return true;
      }

      let minOneValid = false;
      if (option.amountInKilogrammMandatory && optionSelection.amountInKilogramm > 0) {
        console.log('a valid');
        minOneValid = true;
      }
      if (option.amountInLiterMandatory && optionSelection.amountInLiter > 0) {
        console.log('b valid');
        minOneValid = true;
      }
      if (option.amountOthersMandatory && optionSelection.amountOthers != null && optionSelection.amountOthers.trim().length > 0) {
        console.log('c valid');
        minOneValid = true;
      }
      if (option.amountOfPallettesMandatory && optionSelection.amountOfPallettes > 0) {
        console.log('d valid');
        minOneValid = true;
      }
      if (option.amountOfBoxesMandatory && optionSelection.amountOfBoxes > 0) {
        console.log('e valid');
        minOneValid = true;
      }
      if (!minOneValid) {
        return false;
      }
    }

    return true;
  }

  private checkParameterIsValid(option: TransportOption) {

    if (!this.parametersEnabled || !this.parametersMandatory) {
      return true;
    }

    const optionSelection = this.findSelection(option.id);
    if (optionSelection != null && optionSelection.selected) {

      for (const parameter of option.parameter) {
        if (parameter.mandatory && parameter.show) {
          const parameterSelection = optionSelection.parameter.find(p => p.parameterId === parameter.id);
          if (parameterSelection == null || parameterSelection.values == null || parameterSelection.values.length === 0) {
            return false;
          }
          if (parameter.type === 'TEXT' && parameterSelection.values[0].trim().length < (parameter.minLength || 3)) {
            return false;
          }
        }
      }
    }

    return true;
  }

  private findSelection(optionId: number): TransportOptionSelection {
    return this.selection.find(s => s.id === optionId);
  }

  notifySelectionChange() {
    this.selectionChange.next();
  }
}

export class TransportOptionCondition {

  id?: string;

  constructor(constraint?: Partial<TransportOptionCondition>) {
    if (constraint != null) {
      this.id = constraint.id;
    }
  }

}

export class TransportOptionColumn {

  options = new Array<TransportOption>();

  constructor(column?: any) {
    if (column != null) {
      if (column.options != null) {
        for (const option of column.options) {
          this.options.push(new TransportOption(option));
        }
      }
    }
  }

}

export class TransportOption {

  id: number;
  externalKey: string;
  nameDictionaryKey: string;
  hintDriverDictionaryKey: string;
  allwaysShowHint: boolean;
  optional: boolean;
  minimumSelectedOptions: number;
  columnIndex: number;
  rowIndex: number;
  onlyInternal: boolean;
  color: string;
  messagingReference: string;
  amountOfPallettesMandatory: boolean;
  amountOfBoxesMandatory: boolean;
  amountInKilogrammMandatory: boolean;
  amountInLiterMandatory: boolean;
  amountOthersMandatory: boolean;

  amountMandatoryInTerminal: boolean;
  amountMandatoryInTime: boolean;

  readOnlyAtTerminal: boolean;

  parameter = new Array<TransportOptionParameter>();

  options = new Array<TransportOption>();

  constructor(option?: any) {
    if (option != null) {

      this.id = option.id;
      this.externalKey = option.externalKey;
      this.nameDictionaryKey = option.nameDictionaryKey;
      this.hintDriverDictionaryKey = option.hintDriverDictionaryKey;
      this.optional = option.optional;
      this.minimumSelectedOptions = option.minimumSelectedOptions;
      this.columnIndex = option.columnIndex;
      this.rowIndex = option.rowIndex;
      this.onlyInternal = option.onlyInternal;
      this.color = option.color;
      this.messagingReference = option.messagingReference;
      this.amountOfPallettesMandatory = option.amountOfPallettesMandatory;
      this.amountOfBoxesMandatory = option.amountOfBoxesMandatory;
      this.amountInKilogrammMandatory = option.amountInKilogrammMandatory;
      this.amountInLiterMandatory = option.amountInLiterMandatory;
      this.amountOthersMandatory = option.amountOthersMandatory;

      this.amountMandatoryInTerminal = option.amountMandatoryInTerminal;
      this.amountMandatoryInTime = option.amountMandatoryInTime;

      this.allwaysShowHint = option.allwaysShowHint;

      this.readOnlyAtTerminal = option.readOnlyAtTerminal;

      if (option.options != null) {
        for (const subOption of option.options) {
          this.options.push(new TransportOption(subOption));
        }
      }

      if (option.parameter != null) {
        for (const parameter of option.parameter) {
          this.parameter.push(new TransportOptionParameter(parameter));
        }
      }

      // temp workaround da im alten Model noch anders benannt
      if (option.transportOptions != null) {
        for (const subOption of option.transportOptions) {
          this.options.push(new TransportOption(subOption));
        }
      }

    }
  }

}

export class TransportOptionParameter {

  id: string;
  type: string;

  sortOrder: string;
  minLength: number;
  show: boolean;
  mandatory: boolean;

  parameterValues = new Array<TransportOptionParameterValue>();

  constructor(parameter?: any) {
    if (parameter != null) {
      this.id = parameter.id;
      this.type = parameter.type;
      this.sortOrder = parameter.sortOrder;
      this.minLength = parameter.minLength;
      this.show = parameter.show;
      this.mandatory = parameter.mandatory;
      if (parameter.parameterValues != null) {
        for (const value of parameter.parameterValues) {
          this.parameterValues.push(new TransportOptionParameterValue(value));
        }
      }
    }
  }

}

export class TransportOptionSelection {

  selected = true;
  fixed = false;

  id: number;

  amountOfPallettes: number;
  amountOfBoxes: number;
  amountInKilogramm: number;
  amountInLiter: number;
  amountOthers: string;

  parameter = new Array<TransportOptionParameterSelection>();

  constructor(selection?: any) {
    if (selection != null) {
      this.amountOfPallettes = selection.amountOfPallettes;
      this.amountOfBoxes = selection.amountOfBoxes;
      this.amountInKilogramm = selection.amountInKilogramm;
      this.amountInLiter = selection.amountInLiter;
      this.amountOthers = selection.amountOthers;
      this.id = selection.id;
      if (selection.parameter != null) {
        for (const parameter of selection.parameter) {
          this.parameter.push(new TransportOptionParameterSelection(parameter));
        }
      }
      this.selected = selection.selected || true;
      this.fixed = selection.fixed;
    }
  }

}

export class TransportOptionParameterSelection {

  id: string | undefined;
  parameterId: string | undefined;
  values = new Array<string>();

  constructor(parameter?: Partial<TransportOptionParameterSelection>) {
    if (parameter) {
      this.id = parameter.id;
      this.parameterId = parameter.parameterId;
      if (parameter.values) {
        this.values.push(...parameter.values);
      }
    }
  }

}

export class TransportOptionConstraint {

  id: number;
  name: string;
  count: number;
  type = TransportOptionConstraintType.MIN_SELECTED_COUNT;
  transportOptionIds = new Array<number>();

  constructor(constraint?: any) {
    if (constraint != null) {
      this.id = constraint.id;
      this.name = constraint.name;
      this.count = constraint.count;
      this.type = constraint.type;
      if (constraint.transportOptionIds != null) {
        this.transportOptionIds.push(...constraint.transportOptionIds);
      }
    }
  }

}

export class TransportOptionParameterValue {

  id: number;

  constructor(value?: any) {
    if (value != null) {
      this.id = value.id;
    }
  }

}
